/**
 * Leveling Dashboard
 * 
 * Main entry point for the Leveling capability.
 * Shows summary stats for the 5-level altitude system.
 */

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { 
  Grid3x3,
  AlertTriangle,
  CheckCircle,
  TrendingUp,
  Layers,
  Target,
  ArrowRight,
  BarChart3,
  Clock,
  Eye,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { 
  ALTITUDE_LABELS,
  ALTITUDE_LEVELS_ORDERED,
  validateAllLevelingConstraints,
} from '../../../lib/altitudeValidation';
import { computeAltitudeLevel, legacyAltitudeToLevel } from '../../../lib/altitudePrediction';
import { ConstraintPanel } from '../../../components/prioritization/leveling/ConstraintPanel';
import type { AltitudeLevel } from '../../../types/prioritization/itemLifecycle';
import type { AltitudeLifecycleState } from '../../../types/prioritization/leveling';
import type { Candidate } from '../../../types/prioritization/lifecycle';

// Examples of constraint violations (for in-app reference)
const CONSTRAINT_EXAMPLES: { type: string; example: string }[] = [
  { type: 'Needs Parent or Container', example: 'Level 4–5 item with no parent or strategic bucket' },
  { type: 'Missing Completion Requirements', example: 'Program/Strategic item with no success definition, first deliverable, or owner' },
  { type: 'Altitude Mismatch', example: 'Declared level inconsistent with time horizon, work size, or impact' },
  { type: 'Invalid Parent–Child Gap', example: 'Child at same/higher level than parent, or gap > 2 levels' },
  { type: 'Task Cannot Have Children', example: 'Level 1 (Tactical) item that has child items' },
];

// ============================================================================
// Helper Functions
// ============================================================================

function getCandidateAltitudeLevel(candidate: Candidate): AltitudeLevel {
  if (candidate.altitudeLevel) {
    return candidate.altitudeLevel;
  }
  if (candidate.altitudeScopingInputs) {
    return computeAltitudeLevel(candidate.altitudeScopingInputs);
  }
  const legacyAltitude = candidate.canonicalAltitude || candidate.altitude;
  if (legacyAltitude) {
    return legacyAltitudeToLevel(legacyAltitude) || 3;
  }
  return 3;
}

// ============================================================================
// Sub-components
// ============================================================================

function StatCard({ 
  title, 
  value, 
  icon, 
  color = 'primary',
  subtitle,
}: { 
  title: string; 
  value: number | string;
  icon: React.ReactNode;
  color?: 'primary' | 'green' | 'amber' | 'red';
  subtitle?: string;
}) {
  const colorClasses = {
    primary: 'bg-primary-50 text-primary-600',
    green: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
    amber: 'bg-[var(--mustard-dim)] text-[var(--mustard)]',
    red: 'bg-[var(--coral-dim)] text-[var(--coral)]',
  };
  
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-[var(--text-ter)]">{title}</p>
          <p className="text-3xl font-bold text-[var(--text-pri)] mt-1">{value}</p>
          {subtitle && <p className="text-sm text-[var(--text-ter)] mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3  ${colorClasses[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function AltitudeLevelDistribution({ 
  byLevel 
}: { 
  byLevel: Record<AltitudeLevel, number> 
}) {
  const total = Object.values(byLevel).reduce((a, b) => a + b, 0);
  
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
      <h3 className="font-semibold text-[var(--text-pri)] mb-4 flex items-center gap-2">
        <Layers className="h-5 w-5 text-[var(--text-ter)]" />
        Distribution by Altitude Level
      </h3>
      
      <div className="space-y-3">
        {ALTITUDE_LEVELS_ORDERED.map(level => {
          const count = byLevel[level] || 0;
          const percentage = total > 0 ? (count / total) * 100 : 0;
          
          return (
            <div key={level}>
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="flex items-center gap-2 text-[var(--text-sec)]">
                  <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary-100 text-primary-700 text-xs font-bold">
                    {level}
                  </span>
                  {ALTITUDE_LABELS[level]}
                </span>
                <span className="text-[var(--text-ter)]">{count} ({percentage.toFixed(0)}%)</span>
              </div>
              <div className="h-2 bg-[var(--hover-tint)] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary-500 transition-all duration-300"
                  style={{ width: `${percentage}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LifecycleStateDistribution({ 
  byState 
}: { 
  byState: Record<AltitudeLifecycleState, number> 
}) {
  const stateConfig: Record<AltitudeLifecycleState, { label: string; pillBg: string; pillText: string; barBg: string; icon: React.ReactNode }> = {
    proposed: { 
      label: 'Proposed', 
      pillBg: 'bg-[var(--mustard-dim)]', 
      pillText: 'text-[var(--mustard)]', 
      barBg: 'bg-[var(--mustard)]', 
      icon: <Clock className="h-4 w-4 flex-shrink-0" /> 
    },
    under_review: { 
      label: 'Under Review', 
      pillBg: 'bg-[var(--slate-dim)]', 
      pillText: 'text-[var(--text-sec)]', 
      barBg: 'bg-[var(--slate)]', 
      icon: <Eye className="h-4 w-4 flex-shrink-0" /> 
    },
    accepted: { 
      label: 'Accepted', 
      pillBg: 'bg-[var(--green-dim)]', 
      pillText: 'text-[var(--green)] dark:text-[var(--text-ter)]', 
      barBg: 'bg-[var(--green)]', 
      icon: <CheckCircle className="h-4 w-4 flex-shrink-0" /> 
    },
  };
  
  const total = Object.values(byState).reduce((a, b) => a + b, 0);
  const states = (Object.keys(stateConfig) as AltitudeLifecycleState[]);
  
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 shadow-sm">
      <h3 className="font-semibold text-[var(--text-pri)] mb-5 flex items-center gap-2">
        <Target className="h-5 w-5 text-[var(--text-ter)]" />
        Altitude Lifecycle States
      </h3>
      
      {/* Equal-width columns: no overlap, clear separation */}
      <div className="grid grid-cols-3 gap-4">
        {states.map(state => {
          const count = byState[state] || 0;
          const config = stateConfig[state];
          const pct = total > 0 ? (count / total) * 100 : 0;
          return (
            <div
              key={state}
              className="min-w-0 flex flex-col border border-[var(--rule)] bg-[var(--surface)]/50 p-4"
            >
              <div className={`flex items-center justify-center gap-2 px-3 py-2 rounded-full text-sm font-medium w-fit mx-auto ${config.pillBg} ${config.pillText}`}>
                {config.icon}
                <span className="whitespace-nowrap">{config.label}</span>
              </div>
              <p className="text-2xl font-bold text-[var(--text-pri)] mt-3 text-center tabular-nums">{count}</p>
              {total > 0 && (
                <div className="mt-2 h-2 bg-[var(--hover-tint)] rounded-full overflow-hidden">
                  <div 
                    className={`h-full ${config.barBg} rounded-full transition-all duration-300 min-w-0`}
                    style={{ width: `${Math.max(pct, pct > 0 ? 6 : 0)}%` }}
                    title={`${config.label}: ${count} (${pct.toFixed(0)}%)`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {total > 0 && (
        <div className="mt-5 pt-4 border-t border-[var(--rule)]">
          <div className="flex items-center justify-between gap-3 mb-2">
            <span className="text-xs font-medium text-[var(--text-ter)] uppercase tracking-wide">Total</span>
            <span className="text-sm font-semibold text-[var(--text-sec)]">{total} item{total !== 1 ? 's' : ''}</span>
          </div>
          <div className="h-2.5 bg-[var(--hover-tint)] rounded-full overflow-hidden flex">
            {states.map(state => {
              const count = byState[state] || 0;
              const percentage = (count / total) * 100;
              const config = stateConfig[state];
              return (
                <div 
                  key={state}
                  className={`h-full ${config.barBg} transition-all duration-300 first:rounded-l-full last:rounded-r-full`}
                  style={{ width: `${percentage}%` }}
                  title={`${config.label}: ${count} (${percentage.toFixed(0)}%)`}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function QuickActions() {
  return (
    <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6">
      <h3 className="font-semibold text-[var(--text-pri)] mb-4">Quick Actions</h3>
      
      <div className="space-y-3">
        <Link
          to="/prioritization/leveling/grid"
          className="flex items-center justify-between p-4 bg-primary-50 hover:bg-primary-100 transition-colors"
        >
          <div className="flex items-center gap-3">
            <Grid3x3 className="h-5 w-5 text-primary-600" />
            <div>
              <p className="font-medium text-primary-900">Open 5x6 Grid</p>
              <p className="text-sm text-primary-700">5 levels x 6 item categories</p>
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-primary-600" />
        </Link>
        
        <Link
          to="/prioritization/leveling/constraints"
          className="flex items-center justify-between p-4 bg-[var(--surface)] hover:bg-[var(--hover-tint)] transition-colors"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-[var(--text-sec)]" />
            <div>
              <p className="font-medium text-[var(--text-pri)]">Review Constraints</p>
              <p className="text-sm text-[var(--text-sec)]">Fix structural violations</p>
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-[var(--text-ter)]" />
        </Link>
        
        <Link
          to="/prioritization/shape/queue"
          className="flex items-center justify-between p-4 bg-[var(--surface)] hover:bg-[var(--hover-tint)] transition-colors"
        >
          <div className="flex items-center gap-3">
            <TrendingUp className="h-5 w-5 text-[var(--text-sec)]" />
            <div>
              <p className="font-medium text-[var(--text-pri)]">Shape Items</p>
              <p className="text-sm text-[var(--text-sec)]">Set dimensions during item work</p>
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-[var(--text-ter)]" />
        </Link>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function LevelingDashboard() {
  const items = useItemsStore(state => state.items);
  
  // Calculate summary stats using new level-based system
  const summary = useMemo(() => {
    const byAltitudeLevel: Record<AltitudeLevel, number> = {
      5: 0,
      4: 0,
      3: 0,
      2: 0,
      1: 0,
    };
    
    const byCandidateType: Record<string, number> = {};
    
    const byLifecycleState: Record<AltitudeLifecycleState, number> = {
      proposed: 0,
      under_review: 0,
      accepted: 0,
    };
    
    for (const c of items) {
      // By altitude level
      const level = getCandidateAltitudeLevel(c);
      byAltitudeLevel[level] = (byAltitudeLevel[level] || 0) + 1;
      
      // By item category
      const type = c.itemCategory || 'unset';
      byCandidateType[type] = (byCandidateType[type] || 0) + 1;
      
      // By lifecycle state
      const state = c.altitudeLifecycleState || 'proposed';
      byLifecycleState[state] = (byLifecycleState[state] || 0) + 1;
    }
    
    // Get violations
    const violations = validateAllLevelingConstraints(items);
    
    return {
      totalItems: items.length,
      byAltitudeLevel,
      byCandidateType,
      byLifecycleState,
      violationCount: violations.summary.total,
      pendingReviewCount: byLifecycleState.proposed + byLifecycleState.under_review,
    };
  }, [items]);
  
  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Layers className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Level"
        subtitle={`${summary.totalItems} item${summary.totalItems !== 1 ? 's' : ''} · Ensure comparable altitudes before prioritization`}
      />
      
      {/* Stats grid */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard
          title="Total Items"
          value={summary.totalItems}
          icon={<BarChart3 className="h-6 w-6" />}
          color="primary"
        />
        <StatCard
          title="Accepted Altitudes"
          value={summary.byLifecycleState.accepted}
          icon={<CheckCircle className="h-6 w-6" />}
          color="green"
          subtitle={`${summary.totalItems > 0 ? ((summary.byLifecycleState.accepted / summary.totalItems) * 100).toFixed(0) : 0}% of total`}
        />
        <StatCard
          title="Pending Review"
          value={summary.pendingReviewCount}
          icon={<Clock className="h-6 w-6" />}
          color="amber"
        />
        <StatCard
          title="Constraint Violations"
          value={summary.violationCount}
          icon={<AlertTriangle className="h-6 w-6" />}
          color={summary.violationCount > 0 ? 'red' : 'green'}
        />
      </div>
      
      {/* Main content grid */}
      <div className="grid grid-cols-3 gap-6">
        {/* Left column - distributions */}
        <div className="space-y-6">
          <AltitudeLevelDistribution byLevel={summary.byAltitudeLevel} />
          <LifecycleStateDistribution byState={summary.byLifecycleState} />
        </div>
        
        {/* Middle column - constraint panel preview + examples */}
        <div className="col-span-1 space-y-6">
          <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[var(--text-pri)]">Constraint Violations</h3>
              <Link
                to="/prioritization/leveling/constraints"
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                View All
              </Link>
            </div>
            <ConstraintPanel compact />
          </div>
          <details className="bg-[var(--bg-raised)] border border-[var(--rule)] shadow-sm group">
            <summary className="p-4 cursor-pointer list-none font-medium text-[var(--text-pri)] flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-[var(--mustard)]" />
              Constraint violation examples
            </summary>
            <div className="px-4 pb-4 pt-0">
              <ul className="space-y-2 text-sm text-[var(--text-sec)]">
                {CONSTRAINT_EXAMPLES.map(({ type, example }) => (
                  <li key={type} className="flex flex-col gap-0.5">
                    <span className="font-medium text-[var(--text-pri)]">{type}</span>
                    <span className="text-[var(--text-ter)]">{example}</span>
                  </li>
                ))}
              </ul>
            </div>
          </details>
        </div>
        
        {/* Right column - quick actions */}
        <div>
          <QuickActions />
        </div>
      </div>
    </div>
  );
}

export default LevelingDashboard;
